import type { SeedScenario } from './seedLatinScenarios'

/**
 * The scenario where the numbers are the point.
 *
 * Renting a flat, because it is the exchange that turns a misheard number into
 * money: a deposit, a rent, a move-in date, and someone standing in front of you
 * who has other viewings booked. Every beat forces a figure out of you or at you,
 * and one of them deliberately gives you a number worth checking rather than
 * accepting — which is the habit these islands exist to build.
 *
 * The landlord speaks a little above the learner's level throughout. Real ones
 * do, and a scenario that moderates is not practising the thing that actually
 * goes wrong.
 */
export const NUMBER_SCENARIOS: Record<string, SeedScenario[]> = {
  de: [
    {
      slug: 'wohnung',
      title: 'The numbers that cost money',
      nativeTitle: 'Die Wohnungsbesichtigung',
      setting: 'Eine Wohnungsbesichtigung, Samstagvormittag, drei andere warten unten',
      brief:
        'A flat viewing on a Saturday morning with three other people waiting downstairs. The landlord is friendly and quick, and every figure he says matters — one of them is worth checking rather than nodding at.',
      beats: [
        {
          speaker: 'Vermieter',
          prompt: 'Schön, dass Sie da sind. Was möchten Sie zuerst wissen?',
          english: 'Good of you to come. What would you like to know first?',
          cue: 'Ask what it comes to altogether.',
          island: 'de_zahlen',
          blocks: ['de_zahlen_b1'],
          hint: 'zusammen is what gets you the total rather than the base rent.',
        },
        {
          speaker: 'Vermieter',
          prompt: 'Also, die Kaltmiete liegt bei neunhundertdreißig, dazu kommen noch die Nebenkosten.',
          english: 'So, the base rent is nine hundred and thirty, plus the utilities on top.',
          cue: 'You are not certain whether he said thirteen or thirty at the end.',
          island: 'de_zahlen',
          blocks: ['de_zahlen_b3'],
          hint: 'Checking is normal here. Guessing is what costs you.',
        },
        {
          speaker: 'Vermieter',
          prompt: 'Dreißig, ja. Neunhundertdreißig kalt.',
          english: 'Thirty, yes. Nine hundred and thirty cold.',
          cue: 'Get that number in writing before you forget it.',
          island: 'de_zahlen',
          blocks: ['de_zahlen_b4'],
        },
        {
          speaker: 'Vermieter',
          prompt: 'Natürlich. Und ab wann bräuchten Sie die Wohnung?',
          english: 'Of course. And from when would you need the flat?',
          cue: 'You arrive on the fourteenth of March.',
          island: 'de_datum',
          blocks: ['de_datum_b2'],
          hint: 'am vierzehnten — the ordinal takes the -en ending after am.',
        },
        {
          speaker: 'Vermieter',
          prompt: 'Das ginge. Wann könnten Sie denn zur zweiten Besichtigung kommen?',
          english: 'That could work. When could you come for a second viewing?',
          cue: 'Weekdays suit you.',
          island: 'de_datum',
          blocks: ['de_datum_b4'],
        },
      ],
    },
  ],

  fr: [
    {
      slug: 'appartement',
      title: 'The numbers that cost money',
      nativeTitle: "La visite de l'appartement",
      setting: "Une visite d'appartement, samedi matin, trois autres personnes attendent en bas",
      brief:
        'A flat viewing on a Saturday morning with three other people waiting downstairs. The landlord is friendly and fast, and every figure matters — one of them is worth checking rather than nodding at.',
      beats: [
        {
          speaker: 'Le propriétaire',
          prompt: 'Bonjour, entrez. Qu’est-ce que vous voulez savoir en premier ?',
          english: 'Hello, come in. What would you like to know first?',
          cue: 'Ask what it comes to altogether.',
          island: 'fr_chiffres',
          blocks: ['fr_chiffres_b1'],
          hint: 'en tout is what gets you the total rather than the bare rent.',
        },
        {
          speaker: 'Le propriétaire',
          prompt: 'Alors, le loyer est de neuf cent soixante-dix, charges comprises.',
          english: 'So, the rent is nine hundred and seventy, charges included.',
          cue: 'You are not sure whether that was sixty or seventy.',
          island: 'fr_chiffres',
          blocks: ['fr_chiffres_b3'],
          hint: 'soixante-dix is literally sixty-ten, which is exactly why the two run together.',
        },
        {
          speaker: 'Le propriétaire',
          prompt: 'Soixante-dix. Neuf cent soixante-dix euros.',
          english: 'Seventy. Nine hundred and seventy euros.',
          cue: 'Get it written down.',
          island: 'fr_chiffres',
          blocks: ['fr_chiffres_b4'],
        },
        {
          speaker: 'Le propriétaire',
          prompt: 'Bien sûr. Et à partir de quand cherchez-vous ?',
          english: 'Of course. And from when are you looking?',
          cue: 'You arrive on the fourteenth of March.',
          island: 'fr_dates',
          blocks: ['fr_dates_b2'],
          hint: 'le quatorze mars — no ordinal and no preposition before the month.',
        },
        {
          speaker: 'Le propriétaire',
          prompt: 'C’est jouable. Vous seriez libre quand pour une deuxième visite ?',
          english: 'That is doable. When would you be free for a second viewing?',
          cue: 'Weekdays work for you.',
          island: 'fr_dates',
          blocks: ['fr_dates_b4'],
        },
      ],
    },
  ],

  es: [
    {
      slug: 'piso',
      title: 'The numbers that cost money',
      nativeTitle: 'La visita al piso',
      setting: 'Una visita a un piso, sábado por la mañana, tres personas más esperando abajo',
      brief:
        'A flat viewing on a Saturday morning with three other people waiting downstairs. The landlord is friendly and quick, and every figure matters — one of them is worth checking rather than nodding at.',
      beats: [
        {
          speaker: 'El casero',
          prompt: 'Buenos días, pase. ¿Qué quiere saber primero?',
          english: 'Good morning, come in. What would you like to know first?',
          cue: 'Ask what it comes to altogether.',
          island: 'es_numeros',
          blocks: ['es_numeros_b1'],
          hint: 'en total is what gets you the whole figure rather than the base rent.',
        },
        {
          speaker: 'El casero',
          prompt: 'Pues son novecientos setenta al mes, comunidad incluida.',
          english: 'Well, it is nine hundred and seventy a month, service charge included.',
          cue: 'You are not sure whether that was sixty or seventy.',
          island: 'es_numeros',
          blocks: ['es_numeros_b3'],
          hint: 'sesenta and setenta differ by one consonant, and he is not slowing down.',
        },
        {
          speaker: 'El casero',
          prompt: 'Setenta. Novecientos setenta.',
          english: 'Seventy. Nine hundred and seventy.',
          cue: 'Get it written down.',
          island: 'es_numeros',
          blocks: ['es_numeros_b4'],
        },
        {
          speaker: 'El casero',
          prompt: 'Claro. ¿Y para cuándo lo necesitaría?',
          english: 'Of course. And when would you need it from?',
          cue: 'You arrive on the fourteenth of March.',
          island: 'es_fechas',
          blocks: ['es_fechas_b2'],
          hint: 'el catorce de marzo — a plain number, no ordinal.',
        },
        {
          speaker: 'El casero',
          prompt: 'Se puede hacer. ¿Cuándo le vendría bien una segunda visita?',
          english: 'That can be done. When would a second viewing suit you?',
          cue: 'Weekdays suit you.',
          island: 'es_fechas',
          blocks: ['es_fechas_b4'],
        },
      ],
    },
  ],

  it: [
    {
      slug: 'appartamento',
      title: 'The numbers that cost money',
      nativeTitle: "La visita all'appartamento",
      setting: 'Una visita a un appartamento, sabato mattina, altre tre persone aspettano giù',
      brief:
        'A flat viewing on a Saturday morning with three other people waiting downstairs. The landlord is friendly and fast, and every figure matters — one of them is worth checking rather than nodding at.',
      beats: [
        {
          speaker: 'Il proprietario',
          prompt: 'Buongiorno, prego, entri. Cosa vuole sapere per prima cosa?',
          english: 'Good morning, come in. What would you like to know first?',
          cue: 'Ask what it comes to altogether.',
          island: 'it_numeri',
          blocks: ['it_numeri_b1'],
          hint: "Quant'è in tutto — the elision is what makes it sound spoken rather than read.",
        },
        {
          speaker: 'Il proprietario',
          prompt: 'Allora, sono novecentosettanta al mese, spese comprese.',
          english: 'So, it is nine hundred and seventy a month, charges included.',
          cue: 'You are not sure whether that was sixty or seventy.',
          island: 'it_numeri',
          blocks: ['it_numeri_b3'],
          hint: 'sessanta against settanta — a doubled consonant apart, and Italian doubles both.',
        },
        {
          speaker: 'Il proprietario',
          prompt: 'Settanta. Novecentosettanta.',
          english: 'Seventy. Nine hundred and seventy.',
          cue: 'Get it written down.',
          island: 'it_numeri',
          blocks: ['it_numeri_b4'],
        },
        {
          speaker: 'Il proprietario',
          prompt: 'Certo. E da quando le servirebbe?',
          english: 'Of course. And from when would you need it?',
          cue: 'You arrive on the fourteenth of March.',
          island: 'it_date',
          blocks: ['it_date_b2'],
          hint: 'il quattordici marzo — a plain number, and no preposition before the month.',
        },
        {
          speaker: 'Il proprietario',
          prompt: 'Si può fare. Quando sarebbe libero per una seconda visita?',
          english: 'That can be done. When would you be free for a second viewing?',
          cue: 'Weekdays work for you.',
          island: 'it_date',
          blocks: ['it_date_b4'],
        },
      ],
    },
  ],
}
